<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import {
  formatarMoeda,
  moedaComSimbolo,
  numeroDeMoeda,
} from 'dashboard/helper/moeda';
import {
  obrigacaoParaFormulario,
  nomeDoParceiro,
} from 'dashboard/helper/obrigacaoParaFormulario';
import { chaveDoStatusPrevisto } from 'dashboard/helper/financialStatus';
import { dataEscritorio } from 'dashboard/helper/fusoEscritorio';
import { corDoNome, iniciaisDoNome } from 'dashboard/helper/avatar';
import obligationsAPI from 'dashboard/api/financial/obligations';
import partnersAPI from 'dashboard/api/partners';
import bankAccountsAPI from 'dashboard/api/financial/bankAccounts';
import chartOfAccountsAPI from 'dashboard/api/financial/chartOfAccounts';
import { LegalProcessesApi as legalProcessesAPI } from 'dashboard/api/legalProcesses';
import { LegalClientsApi } from 'dashboard/api/legalClients';
import AdvupSelect from 'dashboard/components-next/AdvupSelect.vue';
import ListaDeAnexos from 'dashboard/components-next/ListaDeAnexos.vue';
import attachmentsAPI from 'dashboard/api/financial/attachments';
import AdvupDateTimeInput from 'dashboard/components-next/AdvupDateTimeInput.vue';

// O FORMULÁRIO DE OBRIGAÇÃO, EM UM LUGAR SÓ.
//
// Ele vivia dentro de financial/Index.vue. Quando o Victor pediu (09/09/2026)
// que a ficha do processo abrisse o mesmo formulário SEM mandar a pessoa para
// o módulo Financeiro, havia dois caminhos: copiar o formulário para lá, ou
// extrair. Copiar criaria dois lugares para editar dinheiro, que divergem no
// primeiro ajuste — e este formulário decide encargo, categoria, conta
// corrente e emissão de cobrança no Asaas.
//
// Então ele saiu do Index e virou este componente. O Index passou a usá-lo
// como qualquer outra tela.
//
// `prefill` é o que a ficha do processo manda: tipo (conforme o bloco de onde
// clicaram), cliente e pasta. Preenchido e EDITÁVEL — pedido do Victor: "já
// tem que estar automaticamente preenchido e não ter bloqueado para
// alteração". Por isso `prefill` só semeia o formulário na abertura; a partir
// daí quem manda é quem está preenchendo.
const props = defineProps({
  open: { type: Boolean, default: false },
  editing: { type: Object, default: null },
  prefill: { type: Object, default: null },
  // O nome com que a pessoa chegou aqui. Na conversa o botão diz "Nova
  // cobrança" e o modal abria como "Nova obrigação" (05/10/2026); no
  // Financeiro, sem título, segue "Nova obrigação".
  titulo: { type: String, default: '' },
});

// `fechou`: a saída terminou — quem desmonta o modal com `v-if` espera por ele (o mesmo do Modal.vue).
const emit = defineEmits([
  'close',
  'saved',
  'criada',
  'needBankAccount',
  'fechou',
]);

const listaDeAnexos = ref(null);

const { t } = useI18n();
const router = useRouter();
const { showAlert } = useAlert();
const F = 'FINANCIAL_MODULE';

// Mesma lista da tela de quitação — o retroativo pago usa o mesmo caminho
// (`settle!`), então a forma de pagamento tem que ser a mesma.
const PAYMENT_METHODS = [
  'pix',
  'ted',
  'boleto',
  'dinheiro',
  'cheque',
  'cartao',
];

const typeLabel = k => t(`${F}.TYPE.${k}`);

// ── estado do formulário e listas de apoio ─────────────────────────────────
const saving = ref(false);
const form = ref(null);
const bankAccounts = ref([]);
const chartOfAccounts = ref([]);

// Só Pix e Boleto. A terceira opção que existia aqui mandava `UNDEFINED` ao
// Asaas — o único valor que libera cartão de crédito e débito ao cliente — e
// chegou a cobrança real duas vezes. Trocar o rótulo dela não bastou; ela
// saiu. O boleto do Asaas já vem com QR Code de Pix, então nada de útil se
// perdeu. Ver `Asaas::BillingConditions`.
const billingTypeOptions = computed(() => [
  { value: 'pix', label: 'Pix' },
  { value: 'boleto', label: 'Boleto' },
]);

const paymentMethodOptions = computed(() =>
  PAYMENT_METHODS.map(k => ({
    value: k,
    label: t(`${F}.PAYMENT_METHOD.${k}`),
  }))
);
const bankAccountOptions = computed(() =>
  bankAccounts.value.map(b => ({ value: b.id, label: b.name }))
);
const chartOfAccountOptions = computed(() => [
  { value: null, label: t(`${F}.NO_CATEGORY`) },
  ...chartOfAccounts.value.map(c => ({ value: c.id, label: c.name })),
]);

// Padrão de encargos do escritório. Mora em LegalOfficeSetting (uma fonte só,
// editável em Configurações do módulo jurídico); aqui a tela só lê e deixa a
// pessoa sobrescrever naquela cobrança.
const encargosPadrao = ref({ interest: null, fine: null, billing: 'pix' });

async function carregarEncargosPadrao() {
  try {
    const { data } = await legalProcessesAPI.getOfficeSetting();
    const s = data.payload || {};
    encargosPadrao.value = {
      interest: s.default_interest_percent ?? null,
      fine: s.default_fine_percent ?? null,
      billing: s.default_billing_type || 'pix',
    };
  } catch {
    // Sem o padrão a tela continua utilizável: os campos nascem vazios e o
    // Asaas aplica o que estiver configurado na conta dele.
    encargosPadrao.value = { interest: null, fine: null, billing: 'pix' };
  }
}

async function carregarListas() {
  try {
    const [contas, categorias] = await Promise.all([
      bankAccountsAPI.getAll(),
      chartOfAccountsAPI.getAll(),
    ]);
    bankAccounts.value = contas.data.payload || [];
    chartOfAccounts.value = categorias.data.payload || [];
  } catch {
    bankAccounts.value = [];
    chartOfAccounts.value = [];
  }
}

// As listas e o padrão de encargos vêm na PRIMEIRA vez que o formulário
// abre (06/10/2026, missão de velocidade). Ele fica montado mesmo fechado no
// Financeiro e na ficha do processo, e buscava contas bancárias e plano de
// contas em toda abertura dessas telas — duas chamadas repetidas no
// Financeiro, que também busca as contas para os filtros.
let carregamentoDasListas = null;
watch(
  () => props.open,
  aberto => {
    if (!aberto || carregamentoDasListas) return;
    carregamentoDasListas = Promise.all([
      carregarEncargosPadrao(),
      carregarListas(),
    ]);
  },
  { immediate: true }
);

// ── busca de cliente (credor/devedor) ──────────────────────────────────────
const contactQuery = ref('');
const contactResults = ref([]);
async function onContactInput() {
  const q = contactQuery.value.trim();
  if (!q) {
    contactResults.value = [];
    return;
  }
  // Obrigação financeira é sempre para Cliente, não qualquer Contato — busca
  // via LegalClientsApi; o resultado ainda grava em creditor_debtor_id
  // (Contact), porque LegalClient belongs_to Contact.
  const { data } = await LegalClientsApi.getAll({ q });
  contactResults.value = (data.payload || [])
    .filter(client => client.contact)
    .map(client => ({
      id: client.contact.id,
      name: client.contact.name,
      // Quem responde pelo cliente, quando ele não responde por si (pasta de
      // menor, curatela). Vem junto porque a cobrança sai NO NOME DELE, e
      // quem está escolhendo precisa saber disso antes de salvar.
      representante: client.representative || null,
    }));
}

// A conta continua sendo do dono da pasta — é dele a dívida e é a pasta dele
// que ela paga. O que muda é quem recebe: o boleto vai com o CPF do
// representante e a régua fala com ele (`Financial::Obligation#pagador`). A
// tela DIZ isso; esconder faria o advogado achar que cobrou a criança.
const quemRecebeACobranca = ref(null);

// 🔴 SEM CPF OU TELEFONE DE QUEM PAGA, NÃO SE GRAVA (Victor, 17/09/2026).
//
// As cinco parcelas da Ana Lúcia foram salvas e nenhuma virou cobrança: a
// ficha não tinha CPF, e o Asaas recusou em segundo plano sem ninguém ver.
// O servidor trava o salvamento (`Financial::DadosDoPagador`); a tela pergunta
// assim que o cliente é escolhido, para o aviso chegar antes de alguém
// preencher o resto.
const pagadorIncompleto = ref(null);

async function conferirPagador() {
  const f = form.value;
  if (
    props.editing ||
    !f?.creditor_debtor_id ||
    f.obligation_type !== 'receivable' ||
    f.retroactive
  ) {
    pagadorIncompleto.value = null;
    return;
  }
  try {
    const { data } = await obligationsAPI.checarPagador({
      contactId: f.creditor_debtor_id,
      obligationType: f.obligation_type,
      retroactive: false,
    });
    pagadorIncompleto.value = data.ok ? null : data;
  } catch {
    // Sem resposta, quem decide é o servidor na hora de salvar.
    pagadorIncompleto.value = null;
  }
}

// A ficha abre em outra aba para o formulário não se perder; ao voltar para
// esta, confere de novo.
function abrirFichaDoPagador() {
  const pagador = pagadorIncompleto.value?.pagador;
  if (!pagador) return;
  const rota = pagador.legal_client_id
    ? {
        name: 'legal_client_show',
        params: { clientId: pagador.legal_client_id },
      }
    : { name: 'contacts_edit', params: { contactId: pagador.id } };
  window.open(router.resolve(rota).href, '_blank', 'noopener');
}

function aoVoltarParaAba() {
  if (pagadorIncompleto.value) conferirPagador();
}

onMounted(() => window.addEventListener('focus', aoVoltarParaAba));
onBeforeUnmount(() => window.removeEventListener('focus', aoVoltarParaAba));

const grauDoRepresentante = grau =>
  grau
    ? t(
        `LEGAL_CLIENT_MODULE.REPRESENTATIVE.RELATIONSHIPS.${grau.toUpperCase()}`
      )
    : '';

// ── pasta (LegalProcess) ───────────────────────────────────────────────────
// A lista mostra SÓ as pastas do cliente escolhido. Busca livre permitiria
// pendurar a cobrança de um cliente na pasta de outro — erro que ninguém
// percebe olhando a tela depois.
const ownerQuery = ref('');
const ownerResults = ref([]);
const ownerLoading = ref(false);

// O escritório lê a pasta como `Proc - 0000187` (paridade Legal One). O CNJ é
// dado do processo, não identidade da pasta.
function ownerLabel(p) {
  return p.folder_number || p.title;
}

// ── parceiro do repasse ────────────────────────────────────────────────────
const partnerQuery = ref('');
const partnerResults = ref([]);
const partnerName = ref('');

// OS PARCEIROS DO PRÓPRIO PROCESSO VÊM PRIMEIRO (Victor, 09/09/2026):
//
//   "lá em proc / envolvidos tem como add o parceiro né? então
//    automaticamente esse mesmo parceiro envolvido nesse proc aparece lá na
//    hora de repasse a parceiros"
//
// Ele está certo, e é melhor do que buscar entre todos: quem lança o repasse
// não digita nada, e não dá para repassar por engano a um parceiro que não
// tem nada com aquele processo.
//
// Não TRAVA nos vinculados: quem precisar de outro parceiro digita e busca
// normalmente. Sugerir é diferente de obrigar.
const partnersDoProcesso = ref([]);

async function carregarPartnersDoProcesso() {
  const processoId = form.value?.owner_id;
  if (!processoId || form.value?.owner_type !== 'LegalProcess') {
    partnersDoProcesso.value = [];
    return;
  }
  try {
    const { data } = await legalProcessesAPI.getPartners(processoId);
    partnersDoProcesso.value = (data.payload || [])
      .map(lpp => lpp.partner)
      .filter(Boolean);
  } catch {
    // Sem os vinculados o campo continua servindo — vira busca livre.
    partnersDoProcesso.value = [];
  }
}

// A lista de parceiros como o servidor devolve: `{ data: [...] }`. Num lugar
// só para os dois caminhos (focar e digitar) não poderem divergir — foi
// exatamente por estarem separados que os dois carregavam o mesmo defeito.
const partnersDaResposta = resposta =>
  (Array.isArray(resposta?.data) && resposta.data) ||
  (Array.isArray(resposta?.payload) && resposta.payload) ||
  (Array.isArray(resposta) && resposta) ||
  [];

// Com o campo vazio, mostra os do processo. Digitando, busca em todos.
async function onPartnerInput() {
  const q = partnerQuery.value.trim();
  if (!q) {
    partnerResults.value = partnersDoProcesso.value;
    return;
  }
  const { data } = await partnersAPI.get();
  // 🔴 `data.data`, e NÃO `data.payload`. O endpoint de parceiros devolve
  // `{ data: [...] }` — é o único do módulo que não usa `payload`. Com
  // `data.payload || data`, o fallback entregava o OBJETO inteiro, o `.filter`
  // estourava e o `catch` devolvia lista vazia: o campo nunca mostrou parceiro
  // nenhum, nem digitando nem ao focar. Victor, 10/09/2026: "nao aparece o
  // dropdown com opcao NENHUMA".
  // A ficha do Processo e a do Cliente já liam `data.data` — só este ponto
  // estava fora do padrão.
  const todos = partnersDaResposta(data);
  partnerResults.value = todos
    .filter(pt => pt.active !== false)
    .filter(pt => pt.name?.toLowerCase().includes(q.toLowerCase()))
    .slice(0, 8);
}

// Ao focar sem ter digitado, aparecem os do processo — é o caso comum e não
// faz sentido exigir uma tecla para ver duas opções.
//
// 🔴 E QUANDO NÃO HÁ NENHUM DO PROCESSO, MOSTRA TODOS (Victor, 10/09/2026:
// "eu quero selecionar parceiro e nao aparece o dropdown com opcao NENHUMA").
// Antes o campo simplesmente não abria: quem edita uma obrigação sem pasta
// vinculada, ou de pasta sem parceiro, ficava diante de um campo mudo, sem
// saber que precisava adivinhar o nome para ele reagir. O escritório tem seis
// parceiros — mostrar a lista é mais barato que ensinar a busca.
async function onPartnerFocus() {
  if (partnerQuery.value.trim()) return;

  if (partnersDoProcesso.value.length) {
    partnerResults.value = partnersDoProcesso.value;
    return;
  }
  try {
    const { data } = await partnersAPI.get();
    partnerResults.value = partnersDaResposta(data)
      .filter(pt => pt.active !== false)
      .slice(0, 8);
  } catch {
    partnerResults.value = [];
  }
}

function pickPartner(pt) {
  form.value.partner_id = pt.id;
  partnerName.value = pt.name;
  partnerResults.value = [];
  partnerQuery.value = '';
}

function clearPartner() {
  form.value.partner_id = null;
  partnerName.value = '';
  partnerResults.value = [];
}

// Conta A PAGAR: só ela tem parceiro de repasse.
const ehAPagar = computed(() => form.value?.obligation_type === 'payable');

// ── o formulário: montar, limpar, escolher ─────────────────────────────────
function blankForm() {
  return {
    obligation_type: 'receivable',
    description: '',
    gross_amount: '',
    due_date: '',
    total_installments: '1',
    bank_account_id: bankAccounts.value[0]?.id || null,
    creditor_debtor_id: null,
    creditor_debtor_name: '',
    owner_type: null,
    owner_id: null,
    owner_label: '',
    chart_of_account_id: null,
    partner_id: null,
    notes: '',
    interest_percent: encargosPadrao.value.interest,
    fine_percent: encargosPadrao.value.fine,
    billing_type: encargosPadrao.value.billing,
    // Lançamento retroativo (Victor, 10/09/2026): registro do que é anterior
    // ao Asaas. Ver o bloco no template e `retroactive` no controller.
    retroactive: false,
    retroactive_paid: true,
    retroactive_realized_at: '',
    retroactive_payment_method: 'pix',
    retroactive_amount: '',
  };
}

// 🔴 O STATUS NÃO É ESCOLHIDO A DEDO.
//
// Quem preenche diz só "pago" ou "em aberto"; o status sai disso cruzado com
// o vencimento, que é a mesma regra do resto do Financeiro
// (`Financial::Obligation#auto_mark_overdue`). Deixar escolher na mão criaria
// "Vencida" com vencimento em 2027 e o painel de inadimplência passaria a
// mentir.
// 🔴 UMA LINHA POR PARCELA (Victor, 10/09/2026): "a Dra. Jullya colocou duas
// parcelas lá em cima, então acho que aí embaixo deveria ter um campo para
// cada parcela, porque provavelmente entrou em datas diferentes".
//
// Vale para os dois lados: parcela paga tem a data em que o dinheiro ENTROU,
// e parcela em aberto tem o vencimento combinado na época — nenhum dos dois
// obedece "mensal a partir do vencimento".
//
// E cada linha decide sozinha se foi paga, porque o caso real é o cliente ter
// pago a primeira e ficado devendo a segunda. O botão Pago/Em aberto lá em
// cima vira um "marcar todas".
const parcelasRetro = ref([]);

const somaMeses = (iso, meses) => {
  if (!iso) return '';
  const [a, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!a || !m || !d) return '';
  const data = new Date(a, m - 1 + meses, 1);
  // Dia 31 em mês de 30 não pode virar o dia 1 do mês seguinte: prende no
  // último dia do mês, que é o que o financeiro entende por "mesma data".
  const ultimo = new Date(data.getFullYear(), data.getMonth() + 1, 0).getDate();
  data.setDate(Math.min(d, ultimo));
  const dois = v => String(v).padStart(2, '0');
  return `${data.getFullYear()}-${dois(data.getMonth() + 1)}-${dois(data.getDate())}`;
};

// Espelha `Financial::InstallmentGenerator`: divide igual e a sobra cai na
// última. Aqui é só para PREENCHER o que a pessoa vai ver e poder corrigir —
// o que for salvo é o que estiver na tela.
function montarParcelasRetro() {
  const total = Math.max(parseInt(form.value?.total_installments, 10) || 1, 1);
  // 🔴 `numeroDeMoeda`, e não um `Number` com `replace` de vírgula.
  // `Number("1.500,00".replace(',', '.'))` é `Number("1.500.00")` = NaN, que o
  // `|| 0` transformava em ZERO: as parcelas do retroativo nasciam R$ 0,00 sem
  // avisar. Ver `helper/moeda`.
  const bruto = numeroDeMoeda(form.value?.gross_amount) || 0;
  const cada = Math.round((bruto / total) * 100) / 100;
  const sobra = Math.round((bruto - cada * total) * 100) / 100;

  parcelasRetro.value = Array.from({ length: total }, (_, i) => ({
    numero: i + 1,
    due_date: somaMeses(form.value?.due_date, i),
    // Vírgula, como o campo de valor lá em cima. "300.00" no meio de um
    // formulário que mostra "600,00" parece dado de outro sistema.
    gross_amount: formatarMoeda(i === total - 1 ? cada + sobra : cada),
    paid: !!form.value?.retroactive_paid,
    realized_at: '',
    payment_method: form.value?.retroactive_payment_method || 'pix',
    // Quanto ENTROU. Nasce igual à parcela; se o cliente pagou atrasado, a
    // pessoa corrige aqui e a diferença vira juros — a parcela não muda.
    amount_paid: formatarMoeda(i === total - 1 ? cada + sobra : cada),
  }));
}

// Formata ao SAIR do campo, não enquanto digita: mexer no texto a cada tecla
// é o jeito clássico de o cursor pular e a pessoa perder o dígito.
const formatarCampoDeValor = campo => {
  const numero = numeroDeMoeda(form.value?.[campo]);
  form.value[campo] = numero === null ? '' : formatarMoeda(numero);
};

const formatarValorDaParcela = linha => {
  const numero = numeroDeMoeda(linha.gross_amount);
  linha.gross_amount = numero === null ? '' : formatarMoeda(numero);
};

// 🔴 A DÍVIDA NÃO CRESCE PORQUE PAGARAM ATRASADO.
//
// Victor, 10/09/2026: a Dra. Jullya tentou lançar R$ 350 numa parcela de
// R$ 300 e o sistema recusou; sem saída, ela inflou as parcelas e a obrigação
// passou a dizer que foram cobrados R$ 680 quando foram R$ 600.
//
// A parcela continua sendo o que foi COBRADO. O que entrou a mais é juros, e
// aparece aqui antes de salvar para ninguém descobrir depois.
const jurosDaLinha = linha => {
  if (!linha?.paid) return 0;
  const devido = numeroDeMoeda(linha.gross_amount) || 0;
  const entrou = numeroDeMoeda(linha.amount_paid);
  if (entrou === null) return 0;
  return Math.max(Math.round((entrou - devido) * 100) / 100, 0);
};

const formatarValorRecebido = linha => {
  const numero = numeroDeMoeda(linha.amount_paid);
  linha.amount_paid = numero === null ? '' : formatarMoeda(numero);
};

// 🔴 UMA PARCELA TAMBÉM PRECISA DO VALOR RECEBIDO (Victor, 10/09/2026:
// "independemente se for uma parcela so precisa ter pq ai que vai dizer se o
// valor la de cima for diferente do recebido em relacao a juros multa etc").
//
// O caminho de VÁRIAS parcelas já tinha o campo em cada linha. O de UMA
// parcela mostrava só "Quando entrou" e "Como entrou" — e o backend, sem
// linhas para ler, quitava com `amount: obligation.gross_amount`. Ou seja: no
// lançamento de uma parcela o valor recebido era FORÇADO a ser igual ao
// devido, e juros ou multa não tinham como ser registrados. O campo
// `retroactive_amount` já existia no formulário desde o início e nunca foi
// desenhado nem enviado.
//
// Mesma conta e mesmo formatador do caminho parcelado, para os dois não
// divergirem: monta uma "linha" com o que o formulário tem.
// 🔴 "RECEBIDO" NUMA CONTA A PAGAR É MENTIRA (Victor, 10/09/2026: "que nao
// deveria ser quanto entrou nesse caso ne ja que eu selecionei la em cima o
// tipo A PAGAR E CATEGORIA REPASSE ENTAO NAO ENTROU MERDA NENHUMA, SAiu").
//
// Ele está certo, e o erro era meu: o bloco de retroativo nasceu pensando em
// honorário (dinheiro entrando) e os rótulos ficaram cravados em "entrou" e
// "recebido". Numa conta a pagar o dinheiro SAI.
//
// Um helper decide pela direção, em vez de duas cópias do bloco: o campo é o
// mesmo, o que muda é a palavra.
// A chave do texto da divisão, num computed em vez de expressão no template —
// mesma razão da guarda acima: o template não pode depender de `form` já
// existir.
const chaveDaDivisao = computed(() =>
  form.value?.obligation_type === 'payable' ? 'SPLIT_OUT' : 'SPLIT'
);

const rotuloRetro = chave => {
  const saida = form.value?.obligation_type === 'payable';
  const mapa = {
    quando: saida ? 'WHEN_OUT' : 'WHEN',
    como: saida ? 'HOW_OUT' : 'HOW',
    valor: saida ? 'PAID_OUT' : 'RECEIVED',
    divisao: saida ? 'SPLIT_OUT' : 'SPLIT',
    caixa: saida ? 'CASH_HINT_OUT' : 'CASH_HINT',
  };
  return t(`${F}.RETROACTIVE.${mapa[chave]}`);
};

// 🔴 `gross_amount`, NÃO `amount`. O campo do formulário se chama
// `gross_amount`; eu escrevi `amount` ao criar este helper hoje, e o efeito foi
// silencioso e grave: o valor devido virava `undefined` → 0, então TODO valor
// recebido aparecia inteiro como juros. Victor, na tela: "ta cobrando como se
// fosse 1725.30 de juros velho por que?". Era isso.
// 🔴 A LISTA DE SUGESTÃO PRECISA SABER FECHAR (Victor, 10/09/2026, testando:
// "esse dropdown do parceiro nao fecha nem a pau enquanto nao escolher o
// parceiro; se eu nao quiser clicar ali agora e preencher outras coisas antes
// eu gostaria que o dropdown aceitasse").
//
// MEDIDO na tela antes de mexer: com a lista de parceiros aberta,
// `document.elementFromPoint` no meio do campo "Buscar cliente..." devolvia um
// botão da lista, e não o campo. Ou seja, não era só incômodo — a lista
// COBRIA o campo de baixo, e escolher um parceiro virava o único jeito de
// seguir preenchendo.
//
// Os três campos de busca deste formulário (parceiro, cliente e pasta) tinham
// o mesmo buraco: a lista só sumia ao escolher. Fecham pelo MESMO caminho,
// num mapa só — três funções quase iguais era como o defeito se espalhou para
// os três desde o começo.
const LISTAS_DE_SUGESTAO = {
  parceiro: partnerResults,
  cliente: contactResults,
  pasta: ownerResults,
};

const fecharSugestoes = qual => {
  LISTAS_DE_SUGESTAO[qual].value = [];
};

// Fechar tem que ter volta: depois do Esc o campo continua com o foco, então
// o `@focus` não dispara de novo e clicar nele não fazia mais nada — campo que
// não responde ao clique é meio conserto. O clique reabre; com a lista já
// aberta, não faz nada.
const reabrirSugestoes = (qual, abrir) => {
  if (LISTAS_DE_SUGESTAO[qual].value.length) return;
  abrir();
};

// 🔴 BAIXA JÁ LANÇADA É FATO, NÃO CAMPO DE FORMULÁRIO (Victor, 10/09/2026:
// "a gente não pode deixar nada em aberto").
//
// Numa cobrança já quitada, o servidor IGNORA de propósito o que vier nestes
// campos — quitar de novo criaria um segundo movimento e creditaria o banco
// duas vezes. Só que deixar os campos editáveis fazia a tela prometer uma
// coisa que não acontece: a pessoa corrige o valor, salva, e nada muda. É o
// mesmo silêncio que acabamos de matar do outro lado.
//
// Então aqui a baixa aparece em modo leitura, com o caminho certo para
// corrigir: fechar e usar "Desfazer quitação", que devolve o dinheiro ao
// saldo da conta antes de lançar de novo.
const baixaJaLancada = computed(() => {
  if (props.editing?.status !== 'settled') return null;

  const mov = (props.editing.movements || []).at(-1);
  const valor = mov?.amount_realized ?? props.editing.amount_received;
  const quando = mov?.realized_at || props.editing.settlement_date;

  return {
    valor: moedaComSimbolo(valor),
    quando: quando ? dataEscritorio(quando) : null,
    como: mov?.payment_method
      ? t(`${F}.PAYMENT_METHOD.${mov.payment_method}`)
      : null,
    juros:
      Number(mov?.surcharge || 0) > 0 ? moedaComSimbolo(mov.surcharge) : null,
  };
});

const linhaDoLancamentoUnico = computed(() => ({
  paid: !!form.value?.retroactive_paid,
  gross_amount: form.value?.gross_amount,
  amount_paid: form.value?.retroactive_amount,
}));

const jurosDoLancamentoUnico = computed(() =>
  jurosDaLinha(linhaDoLancamentoUnico.value)
);

const formatarValorRecebidoUnico = () => {
  const numero = numeroDeMoeda(form.value.retroactive_amount);
  form.value.retroactive_amount = numero === null ? '' : formatarMoeda(numero);
};

const ehParcelado = computed(
  () => (parseInt(form.value?.total_installments, 10) || 1) > 1
);

// Com linhas mistas não existe UM status: o resumo conta quantas ficam em
// cada situação, senão a prévia mentiria para metade das parcelas.
const resumoDasParcelas = computed(() => {
  if (!ehParcelado.value) return [];
  const contagem = {};
  parcelasRetro.value.forEach(linha => {
    const chave = chaveDoStatusPrevisto({
      pago: linha.paid,
      dueDate: linha.due_date,
    });
    contagem[chave] = (contagem[chave] || 0) + 1;
  });
  return Object.entries(contagem).map(([chave, quantas]) => ({
    chave,
    quantas,
    rotulo: t(`${F}.STATUS.${chave}`),
  }));
});

const EXPLICA_STATUS = {
  settled: 'RESULT_SETTLED',
  overdue: 'RESULT_OVERDUE',
  inadimplente: 'RESULT_DEFAULTED',
  pending: 'RESULT_PENDING',
};

const situacaoRetroativa = computed(() => {
  if (!form.value?.retroactive) return null;

  const chave = chaveDoStatusPrevisto({
    pago: form.value.retroactive_paid,
    dueDate: form.value.due_date,
  });

  return {
    chave,
    rotulo: t(`${F}.STATUS.${chave}`),
    explica: t(`${F}.RETROACTIVE.${EXPLICA_STATUS[chave]}`),
  };
});

function closeModal() {
  emit('close');
}

function clearOwner() {
  form.value.owner_type = null;
  form.value.owner_id = null;
  form.value.owner_label = '';
}

// Com cliente escolhido, as pastas dele carregam sozinhas — não faz sentido
// obrigar a digitar para ver duas opções.
async function loadOwnersForContact() {
  const contactId = form.value?.creditor_debtor_id;
  if (!contactId) {
    ownerResults.value = [];
    return;
  }
  ownerLoading.value = true;
  try {
    const { data } = await legalProcessesAPI.search('', {
      contact_id: contactId,
    });
    ownerResults.value = data.payload || [];
  } catch {
    // Sem isto a lista de pastas do devedor abria vazia quando a busca
    // falhava, e o vazio lê como "este contato não tem processo".
    ownerResults.value = [];
  } finally {
    ownerLoading.value = false;
  }
}

async function onOwnerInput() {
  const contactId = form.value?.creditor_debtor_id;
  if (!contactId) return;

  const { data } = await legalProcessesAPI.search(ownerQuery.value.trim(), {
    contact_id: contactId,
  });
  ownerResults.value = data.payload || [];
}

function pickContact(c) {
  form.value.creditor_debtor_id = c.id;
  form.value.creditor_debtor_name = c.name;
  quemRecebeACobranca.value = c.representante || null;
  contactResults.value = [];
  contactQuery.value = '';
  // A pasta pertence ao cliente: trocar de cliente invalida a pasta escolhida.
  clearOwner();
  loadOwnersForContact();
}

function clearContact() {
  form.value.creditor_debtor_id = null;
  form.value.creditor_debtor_name = '';
  quemRecebeACobranca.value = null;
  clearOwner();
  ownerResults.value = [];
}

function pickOwner(p) {
  form.value.owner_type = 'LegalProcess';
  form.value.owner_id = p.id;
  form.value.owner_label = ownerLabel(p);
  ownerResults.value = [];
  ownerQuery.value = '';
  // Trocou de pasta, trocaram os parceiros sugeridos — senão a lista ficaria
  // mostrando os parceiros da pasta anterior.
  clearPartner();
  carregarPartnersDoProcesso();
}

// '' -> null (herda o padrão do escritório) · '0' -> 0 (sem encargo, dito)
//
// Juros e multa são PORCENTAGEM, não dinheiro — mas leem pelo mesmo
// `numeroDeMoeda` de propósito: ele resolve "1,5" e "1.5" igual, que é o que
// uma porcentagem precisa, e assim não sobra nenhuma leitura de número
// escrita à mão no módulo. A varredura de `moedaUnica.spec.js` é o que
// garante que não volte a sobrar.
function valorDeEncargo(v) {
  if (v === '' || v === null || v === undefined) return null;
  const numero = numeroDeMoeda(v);
  return numero === null ? null : String(numero);
}

// A lista é refeita quando o topo muda — valor, vencimento, número de
// parcelas. Quem mexer no topo depois de editar as linhas perde a edição, e
// isso é de propósito: o contrário deixaria uma soma de parcelas diferente do
// valor lá em cima, sem ninguém perceber.
watch(
  () => [
    form.value?.retroactive,
    form.value?.total_installments,
    form.value?.gross_amount,
    form.value?.due_date,
  ],
  () => {
    if (form.value?.retroactive) montarParcelasRetro();
  },
  { deep: false }
);

// O Pago/Em aberto lá em cima é um atalho: marca todas de uma vez, e depois
// cada linha pode ser corrigida.
watch(
  () => form.value?.retroactive_paid,
  valor => {
    parcelasRetro.value.forEach(linha => {
      linha.paid = !!valor;
    });
  }
);

// Semeia o formulário quando o modal abre. Fica em `watch` e não em `onMounted`
// porque o componente vive montado junto da tela e só aparece quando `open`
// vira true — semear no mount pegaria o prefill errado na segunda abertura.
// ── abertura: semeia o formulário com o que veio de fora ───────────────────
watch(
  () => props.open,
  async aberto => {
    if (!aberto) {
      form.value = null;
      return;
    }

    if (!bankAccounts.value.length) await carregarListas();
    if (!bankAccounts.value.length) {
      showAlert(t(`${F}.NEED_BANK_ACCOUNT`));
      emit('needBankAccount');
      emit('close');
      return;
    }

    contactResults.value = [];
    contactQuery.value = '';
    ownerResults.value = [];
    ownerQuery.value = '';

    if (props.editing) {
      // 🔴 MAPA, e não `{ ...blankForm(), ...props.editing }`.
      //
      // O espalhamento perdia categoria, parceiro, credor/devedor e pasta —
      // a API manda objetos aninhados e o formulário guarda ids soltos.
      // Vencimento e valor sobreviviam por serem os dois únicos campos de nome
      // igual dos dois lados, o que fazia o defeito parecer aleatório.
      // Ver `helper/obrigacaoParaFormulario.js`, com teste.
      form.value = obrigacaoParaFormulario(props.editing, blankForm());
      partnerName.value = nomeDoParceiro(props.editing);

      // O bloco de retroativo mostra o que JÁ está gravado, em vez de campos
      // vazios que a pessoa teria de preencher de novo para não perder.
      if (form.value.retroactive) {
        form.value.retroactive_paid = props.editing.status === 'settled';
        form.value.retroactive_realized_at =
          props.editing.settlement_date || '';
        form.value.retroactive_amount = props.editing.amount_received
          ? formatarMoeda(props.editing.amount_received)
          : '';
      }

      if (form.value.creditor_debtor_id) loadOwnersForContact();
      if (form.value.owner_id) carregarPartnersDoProcesso();
      return;
    }

    form.value = blankForm();

    const p = props.prefill;
    if (!p) return;

    if (p.obligation_type) form.value.obligation_type = p.obligation_type;
    // Honorário à vista (06/10/2026): a sugestão da IA abre o formulário com
    // a descrição, o valor calculado e a origem já escritos. Quem lança
    // confere e salva — nada é gravado sem passar por aqui.
    if (p.description) form.value.description = p.description;
    if (p.gross_amount) form.value.gross_amount = String(p.gross_amount);
    if (p.notes) form.value.notes = p.notes;
    if (p.contact_id) {
      form.value.creditor_debtor_id = Number(p.contact_id);
      form.value.creditor_debtor_name = p.contact_name || '';
      // Carrega as pastas do cliente, como se tivessem escolhido pela busca —
      // senão o seletor de pasta viria vazio.
      loadOwnersForContact().then(() => {
        // Aberto pela conversa: cliente com UMA pasta já vem com ela
        // escolhida; com duas ou mais, fica em branco para quem lança dizer.
        const unica = ownerResults.value.length === 1;
        if (p.pasta_unica && unica && !form.value.owner_id) {
          pickOwner(ownerResults.value[0]);
        }
      });
    }
    if (p.owner_id) {
      form.value.owner_type = p.owner_type || 'LegalProcess';
      form.value.owner_id = Number(p.owner_id);
      form.value.owner_label = p.owner_label || '';
      // Já deixa os parceiros daquele processo prontos, para aparecerem no
      // instante em que a pessoa tocar no campo.
      carregarPartnersDoProcesso();
    }
  },
  { immediate: true }
);

watch(
  () => [
    props.open,
    form.value?.creditor_debtor_id,
    form.value?.obligation_type,
    form.value?.retroactive,
  ],
  conferirPagador
);

// ── salvar ─────────────────────────────────────────────────────────────────
async function save() {
  if (pagadorIncompleto.value) {
    showAlert(pagadorIncompleto.value.mensagem);
    return;
  }
  if (!form.value.description.trim()) {
    showAlert(t(`${F}.DESCRIPTION_REQUIRED`));
    return;
  }
  if (!form.value.gross_amount || !form.value.due_date) {
    showAlert(t(`${F}.AMOUNT_DUE_REQUIRED`));
    return;
  }
  saving.value = true;
  const payload = {
    obligation_type: form.value.obligation_type,
    description: form.value.description.trim(),
    // `numeroDeMoeda` e não `replace(',', '.')`: com "1.234,50" o replace
    // devolveria "1.234.50" e o servidor leria 1,23 — mil vezes menos.
    gross_amount: String(numeroDeMoeda(form.value.gross_amount) ?? ''),
    due_date: form.value.due_date,
    total_installments: form.value.total_installments || 1,
    bank_account_id: form.value.bank_account_id,
    creditor_debtor_id: form.value.creditor_debtor_id || null,
    creditor_debtor_name: form.value.creditor_debtor_name || null,
    owner_type: form.value.owner_type || null,
    owner_id: form.value.owner_id || null,
    chart_of_account_id: form.value.chart_of_account_id || null,
    // Só vai parceiro em conta A PAGAR. Trocar o tipo depois de escolher um
    // parceiro não pode deixar o vínculo pendurado numa conta a receber.
    partner_id: ehAPagar.value ? form.value.partner_id || null : null,
    notes: form.value.notes || null,
    // Campo vazio vira null de propósito: null quer dizer "usa o padrão do
    // escritório", enquanto 0 quer dizer "sem encargo nenhum". Mandar 0 no
    // lugar de null faria o Asaas SOBRESCREVER a configuração da conta.
    interest_percent: valorDeEncargo(form.value.interest_percent),
    fine_percent: valorDeEncargo(form.value.fine_percent),
    billing_type: form.value.billing_type || null,
    retroactive: !!form.value.retroactive,
    // Aberto pela conversa: a cobrança deixa uma nota interna lá.
    conversation_id: props.prefill?.conversation_id || undefined,
  };
  // Só viaja quando é retroativo E já foi pago: são instruções para a
  // quitação, não colunas da obrigação.
  if (form.value.retroactive && ehParcelado.value) {
    // O que está na tela é o que é salvo: data e valor de cada linha mandam
    // sobre a régua automática do servidor.
    payload.parcelas = parcelasRetro.value.map(linha => ({
      due_date: linha.due_date,
      gross_amount: String(numeroDeMoeda(linha.gross_amount) ?? ''),
      paid: !!linha.paid,
      realized_at: linha.paid ? linha.realized_at || linha.due_date : null,
      payment_method: linha.paid ? linha.payment_method : null,
      // Quanto ENTROU. O servidor separa o que abate a dívida do que é juros.
      amount_paid: linha.paid
        ? String(numeroDeMoeda(linha.amount_paid) ?? '')
        : null,
    }));
  } else if (form.value.retroactive && form.value.retroactive_paid) {
    payload.retroactive_paid = true;
    payload.retroactive_realized_at =
      form.value.retroactive_realized_at || form.value.due_date;
    payload.retroactive_payment_method = form.value.retroactive_payment_method;
    // Vazio significa "entrou o valor da parcela": o servidor cai no
    // `gross_amount`. Só viaja quando a pessoa digitou algo diferente.
    // 🔴 R$ 1.443,34 IA CRU E O SERVIDOR LIA 1,44 (Victor, 11/09/2026).
    //
    // Este era o ÚNICO campo de dinheiro que viajava como o texto digitado.
    // "1.443,34" chega no Ruby, o `to_d` para no ponto e lê 1.443 — mostrado
    // 1,44. Foi o caminho do print dele: lançamento retroativo, não quitação.
    // Por isso consertar a quitação não consertou este.
    payload.retroactive_amount = numeroDeMoeda(form.value.retroactive_amount);
  }
  try {
    if (props.editing?.id) {
      // 🔴 O AVISO DO SERVIDOR NÃO PODE MORRER AQUI.
      //
      // O `update` salva no advup mesmo quando o Asaas recusa a alteração, e
      // devolve o motivo em `asaas_warning` — de propósito, porque perder a
      // edição inteira por causa de um minuto de API fora do ar seria pior.
      // Só que a tela ignorava o campo e dizia "atualizada": o valor mudava
      // aqui e o boleto na mão do cliente continuava com o número antigo,
      // sem ninguém saber. Era a discordância silenciosa que o
      // `Asaas::ChargeUpdater` existe para evitar, reaparecendo na última
      // etapa. `settle_warning` chega pelo mesmo caminho, quando a baixa
      // pedida no formulário não passa.
      const { data } = await obligationsAPI.update(props.editing.id, payload);
      const aviso = data?.asaas_warning || data?.settle_warning;
      showAlert(
        aviso
          ? t(`${F}.UPDATE_OK_WARN`, { motivo: aviso })
          : t(`${F}.UPDATE_OK`)
      );
    } else {
      const { data } = await obligationsAPI.create(payload);
      const novoId = data?.payload?.[0]?.id ?? null;
      // Os anexos escolhidos vão no PRIMEIRO lançamento (parcelado gera
      // vários; o comprovante é um só). O lançamento já existe: anexo que
      // falhar é avisado pelo nome, não desfaz nada.
      const falharam = novoId
        ? (await listaDeAnexos.value?.enviarPendentes(novoId)) || []
        : [];
      showAlert(
        falharam.length
          ? t(`${F}.SAVE_OK_SEM_ANEXO`, { arquivos: falharam.join(', ') })
          : t(`${F}.SAVE_OK`)
      );
      // Quem abriu pelo Honorário à vista precisa saber QUAL lançamento
      // nasceu, para registrar a sugestão como aceita.
      emit('criada', novoId);
    }
    emit('saved');
    emit('close');
  } catch (error) {
    const recusa = error?.response?.data;
    if (recusa?.pagador_incompleto) {
      pagadorIncompleto.value = recusa.pagador_incompleto;
      showAlert(recusa.pagador_incompleto.mensagem);
      return;
    }
    showAlert(recusa?.errors?.[0] || t(`${F}.SAVE_ERROR`));
  } finally {
    saving.value = false;
  }
}

// PARCEIRO DO REPASSE (Victor, 09/09/2026: "quando selecionar a opção de
// repasse de parceiro deveria aparecer as opções para selecionar qual
// parceiro, não?").
//
// Ele viu a tela com categoria "Repasse de Parceiro" e Credor/Devedor
// preenchido com a CLIENTE — o dinheiro do repasse vai para o parceiro, não
// para ela.
//
// O campo aparece em toda obrigação A PAGAR, e não só quando a categoria é a
// de repasse: amarrar a visibilidade ao nome de uma categoria quebraria no dia
// em que alguém a renomeasse, e nem toda conta a pagar de parceiro
// necessariamente usa aquela categoria. É opcional — nota fiscal e despesa
// fixa também são a pagar e não têm parceiro.
//
// Guardado em `partner_id` e não em `creditor_debtor_id`: aquele aponta para
// Contact, e MEDIDO em produção nenhum dos 5 parceiros tem contato vinculado.
// Criar um Contact por parceiro encheria a agenda de escritórios e captadores
// que ninguém atende.

// ── o que a tela mostra a partir do formulário ─────────────────────────────
// VALOR DE CADA PARCELA (Victor, 09/09/2026, olhando a tela: "cadê mostrando
// o valor de cada parcela também?").
//
// O formulário pede valor TOTAL e número de parcelas, e quem preenche precisa
// fazer a divisão de cabeça para saber o que o cliente vai ver em cada boleto.
// R$ 10.000 em 2x é fácil; R$ 8.750 em 7x já não é — e o erro só aparece
// depois que a cobrança foi emitida.
//
// Nulo (some da tela) quando não há parcelamento ou os números ainda não
// fecham: mostrar "R$ NaN" seria pior que não mostrar nada.
const valorDaParcela = computed(() => {
  // 🔴 R$ 1.500,00 EM 2x MOSTRAVA "R$ 0,75 POR PARCELA" (Victor, 11/09/2026).
  //
  // `parseFloat("1.500,00".replace(',', '.'))` é `parseFloat("1.500.00")`, que
  // o JavaScript lê como 1.5 — e 1,5 ÷ 2 dá 0,75. O número que a pessoa via
  // antes de emitir a cobrança estava mil vezes menor.
  //
  // Havia TRÊS leitores de dinheiro no produto: o `helper/moeda` e estes dois
  // aqui dentro, cada um com a sua conta. Agora é um só.
  const total = numeroDeMoeda(form.value?.gross_amount);
  const qtd = parseInt(form.value?.total_installments, 10);
  if (total === null || !Number.isFinite(qtd) || qtd <= 1) return null;

  return total / qtd;
});

const valorDaParcelaFormatado = computed(() =>
  valorDaParcela.value === null ? null : moedaComSimbolo(valorDaParcela.value)
);

// ── A cara do formulário ───────────────────────────────────────────────────
//
// Victor, 14/09/2026, com a foto do modal: "esse modal eu quero lindo também,
// no estilo desses aí que você está fazendo". Era uma grade de campos cinza,
// com o tipo num seletor, o número de parcelas e os encargos em campo numérico
// do navegador (as setinhas do sistema) e as listas de busca desenhadas à
// mão. O que é SALVO não mudou: `save` e os watchers acima são os mesmos.
//
// O tipo é a primeira escolha e muda o que o resto significa (entrada ou
// saída), então ele vira cartões e pinta o topo.
const TIPOS = [
  { valor: 'receivable', icone: 'i-ph-arrow-down-left-bold', tom: 'teal' },
  { valor: 'payable', icone: 'i-ph-arrow-up-right-bold', tom: 'rubi' },
  { valor: 'transfer', icone: 'i-ph-arrows-left-right-bold', tom: 'azul' },
  { valor: 'reimbursement', icone: 'i-ph-receipt-bold', tom: 'iris' },
];

const tipoAtual = computed(
  () =>
    TIPOS.find(tipo => tipo.valor === form.value?.obligation_type) || TIPOS[0]
);

const classeDoTopo = computed(() =>
  tipoAtual.value.tom === 'azul'
    ? ''
    : `advup-modal__topo--${tipoAtual.value.tom}`
);

// O contador de parcelas: sem as setinhas do navegador, e o campo aceita só
// dígitos — texto torto volta ao último número válido ao sair.
const LIMITE_DE_PARCELAS = 120;
const qtdDeParcelas = computed(() =>
  Math.max(parseInt(form.value?.total_installments, 10) || 1, 1)
);

const mudarParcelas = delta => {
  const proxima = Math.min(
    Math.max(qtdDeParcelas.value + delta, 1),
    LIMITE_DE_PARCELAS
  );
  form.value.total_installments = String(proxima);
};

const acertarParcelas = () => mudarParcelas(0);

// O RESUMO, ao vivo no topo: o que vai ser salvo, dito numa frase — valor,
// parcelas e o primeiro vencimento. Quem preenche confere antes de salvar.
const resumoDoLancamento = computed(() => {
  const total = numeroDeMoeda(form.value?.gross_amount);
  if (total === null || !total || !form.value?.due_date) return '';
  const data = dataEscritorio(form.value.due_date);
  if (qtdDeParcelas.value <= 1) {
    return t(`${F}.FORM_MODAL.SUMMARY_ONE`, {
      valor: moedaComSimbolo(total),
      data,
    });
  }
  return t(`${F}.FORM_MODAL.SUMMARY_MANY`, {
    valor: moedaComSimbolo(total),
    n: qtdDeParcelas.value,
    parcela: moedaComSimbolo(valorDaParcela.value),
    data,
  });
});

// A PRÉVIA DAS PARCELAS: data e valor de cada uma, na mesma conta do servidor
// (`Financial::InstallmentGenerator`: divide igual, a sobra cai na última, um
// mês depois do outro). No retroativo cada linha já é editável lá embaixo, e
// acima de doze a fileira vira sujeira — fica a frase do topo.
const previaDasParcelas = computed(() => {
  if (!form.value || form.value.retroactive) return [];
  const qtd = qtdDeParcelas.value;
  const total = numeroDeMoeda(form.value.gross_amount);
  if (qtd < 2 || qtd > 12 || !total || !form.value.due_date) return [];
  const cada = Math.round((total / qtd) * 100) / 100;
  const sobra = Math.round((total - cada * qtd) * 100) / 100;
  return Array.from({ length: qtd }, (_, i) => ({
    numero: i + 1,
    data: dataEscritorio(somaMeses(form.value.due_date, i)),
    valor: moedaComSimbolo(i === qtd - 1 ? cada + sobra : cada),
  }));
});

// O Pix com o logo dele (Victor, 14/09/2026: "quando for pix, o ícone original
// do pix") — o Phosphor já traz o desenho oficial, sem baixar nada.
const ICONE_DA_COBRANCA = { pix: 'i-ph-pix-logo', boleto: 'i-ph-barcode' };

// O markup usa `editingId` para escolher entre o título "Nova" e "Editar".
const editingId = computed(() => props.editing?.id || null);
</script>

<template>
  <Transition name="advup-saida" @after-leave="emit('fechou')">
    <div v-if="open && form" class="advup-modal-fundo" @click.self="closeModal">
      <!-- Modal criar obrigação. Sai pelo caminho de entrada (`advup-saida`, 29/10/2026);
       o comentário mora DENTRO da raiz — antes dela, o componente vira fragmento.

       🔴 `open && form`, e não só `open` (achado pelo teste de componente em
       10/09/2026). O corpo lê `form.obligation_type`, `form.retroactive` e
       outros SEM proteção, e o `form` só é montado depois que o watcher de
       abertura termina — com um `await` no meio, quando as contas correntes
       ainda não chegaram.

       Em produção passava por sorte de ordem: as contas são carregadas no
       `onMounted`, então o watcher normalmente não espera nada e preenche o
       formulário no mesmo tique. Mas quem clicasse em "Nova obrigação" ANTES
       de a carga terminar — rede lenta, primeiro acesso — pegava o render com
       `form` nulo e o modal estourava. -->
      <!-- `advup-modal--claro` (03/10/2026): o topo claro da Agenda — véu do tom,
         selo sólido, marca d'água. Era o degradê escuro (§9 do padrão). -->
      <div
        v-modal-acessivel="closeModal"
        :data-rascunho="
          editingId
            ? undefined
            : `financeiro:obrigacao:${prefill?.contact_id || 'geral'}`
        "
        class="advup-modal advup-modal--teto advup-modal--claro"
      >
        <!-- O topo leva o tom e o ícone do TIPO, e embaixo do título o resumo
           do que vai ser salvo, ao vivo. -->
        <div class="advup-modal__topo obf-topo" :class="classeDoTopo">
          <i
            :key="`marca-${tipoAtual.valor}`"
            class="advup-modal__marca"
            :class="tipoAtual.icone"
            aria-hidden="true"
          />
          <span class="advup-modal__selo" aria-hidden="true">
            <i
              :key="tipoAtual.valor"
              class="obf-topo__ico"
              :class="tipoAtual.icone"
            />
          </span>
          <div class="min-w-0 flex-1">
            <h2 class="advup-modal__titulo">
              {{ editingId ? t(`${F}.EDIT`) : titulo || t(`${F}.NEW`) }}
            </h2>
            <p
              class="advup-modal__sub advup-modal__sub--solta"
              aria-live="polite"
            >
              {{ resumoDoLancamento || t(`${F}.FORM_MODAL.SUMMARY_EMPTY`) }}
            </p>
          </div>
          <button
            type="button"
            class="advup-modal__fechar"
            :aria-label="t(`${F}.CANCEL`)"
            @click="closeModal"
          >
            <i class="i-ph-x" />
          </button>
        </div>
        <div class="advup-modal__corpo obf">
          <!-- ── O lançamento ─────────────────────────────────────────────── -->
          <section class="advup-secao">
            <h3 class="advup-secao__tit">
              <span class="advup-secao__ico" aria-hidden="true">
                <i class="i-ph-note-pencil" />
              </span>
              {{ t(`${F}.FORM_MODAL.SECTION_WHAT`) }}
            </h3>
            <div
              class="obf-tipos"
              role="radiogroup"
              :aria-label="t(`${F}.FIELD.TYPE`)"
            >
              <button
                v-for="(tipo, i) in TIPOS"
                :key="tipo.valor"
                type="button"
                role="radio"
                class="advup-escolha"
                :class="[
                  `advup-escolha--${tipo.tom}`,
                  { 'advup-escolha--on': form.obligation_type === tipo.valor },
                ]"
                :aria-checked="String(form.obligation_type === tipo.valor)"
                :style="{ '--i': i }"
                @click="form.obligation_type = tipo.valor"
              >
                <span class="advup-escolha__ico" aria-hidden="true">
                  <i :class="tipo.icone" />
                </span>
                <span class="advup-escolha__rot">{{
                  typeLabel(tipo.valor)
                }}</span>
              </button>
            </div>
            <div>
              <label class="advup-rotulo">{{ t(`${F}.FIELD.CATEGORY`) }}</label>
              <AdvupSelect
                v-model="form.chart_of_account_id"
                :options="chartOfAccountOptions"
                min-width="100%"
              />
            </div>
            <div>
              <label for="obf-descricao" class="advup-rotulo">
                {{ t(`${F}.FIELD.DESCRIPTION`) }}
              </label>
              <!-- Campo grande porque este texto é o que o CLIENTE lê no boleto,
                 não uma anotação interna (Victor, 09/09/2026). -->
              <textarea
                id="obf-descricao"
                v-model="form.description"
                rows="3"
                class="advup-campo advup-campo--area reset-base w-full"
                :placeholder="t(`${F}.DESCRIPTION_PLACEHOLDER`)"
              />
              <!-- Com a chave de retroativo ligada não existe cobrança, então
                 dizer "é o texto que o cliente vê" seria mentira. -->
              <p class="advup-txt-nota obf-dica">
                <i
                  :class="form.retroactive ? 'i-ph-lock-simple' : 'i-ph-eye'"
                  aria-hidden="true"
                />
                {{
                  form.retroactive
                    ? t(`${F}.DESCRIPTION_HINT_RETROACTIVE`)
                    : t(`${F}.DESCRIPTION_HINT`)
                }}
              </p>
            </div>
          </section>

          <!-- ── Valor e vencimento ───────────────────────────────────────── -->
          <section class="advup-secao">
            <h3 class="advup-secao__tit">
              <span class="advup-secao__ico" aria-hidden="true">
                <i class="i-ph-calendar-dots" />
              </span>
              {{ t(`${F}.FORM_MODAL.SECTION_WHEN`) }}
            </h3>
            <div class="obf-grade">
              <div>
                <label for="obf-valor" class="advup-rotulo">
                  {{ t(`${F}.FIELD.AMOUNT`) }}
                </label>
                <div class="advup-afixo">
                  <span class="advup-afixo__txt" aria-hidden="true">{{
                    t(`${F}.CURRENCY_SYMBOL`)
                  }}</span>
                  <input
                    id="obf-valor"
                    v-model="form.gross_amount"
                    type="text"
                    inputmode="decimal"
                    class="advup-campo reset-base w-full advup-afixo__campo"
                    :placeholder="t(`${F}.AMOUNT_PLACEHOLDER`)"
                    @blur="formatarCampoDeValor('gross_amount')"
                  />
                </div>
              </div>
              <div>
                <label class="advup-rotulo">{{
                  t(`${F}.FIELD.DUE_DATE`)
                }}</label>
                <AdvupDateTimeInput
                  v-model="form.due_date"
                  date-only
                  input-class="advup-campo reset-base w-full"
                />
              </div>
            </div>
            <div class="obf-grade">
              <div>
                <label for="obf-parcelas" class="advup-rotulo">
                  {{ t(`${F}.FIELD.INSTALLMENTS`) }}
                </label>
                <span class="advup-passo">
                  <button
                    type="button"
                    class="advup-acao advup-acao--p"
                    :disabled="qtdDeParcelas <= 1"
                    :aria-label="t(`${F}.FORM_MODAL.FEWER_INSTALLMENTS`)"
                    @click="mudarParcelas(-1)"
                  >
                    <i class="i-ph-minus-bold" aria-hidden="true" />
                  </button>
                  <input
                    id="obf-parcelas"
                    v-model="form.total_installments"
                    type="text"
                    inputmode="numeric"
                    class="advup-passo__num reset-base"
                    @blur="acertarParcelas"
                  />
                  <button
                    type="button"
                    class="advup-acao advup-acao--p"
                    :disabled="qtdDeParcelas >= LIMITE_DE_PARCELAS"
                    :aria-label="t(`${F}.FORM_MODAL.MORE_INSTALLMENTS`)"
                    @click="mudarParcelas(1)"
                  >
                    <i class="i-ph-plus-bold" aria-hidden="true" />
                  </button>
                </span>
                <!-- Quem preenche informa o TOTAL; quem paga vê a parcela. -->
                <p
                  v-if="valorDaParcelaFormatado && !previaDasParcelas.length"
                  class="advup-txt-nota obf-dica"
                >
                  {{
                    t(`${F}.FIELD.INSTALLMENT_VALUE`, {
                      value: valorDaParcelaFormatado,
                    })
                  }}
                </p>
              </div>
              <div>
                <label class="advup-rotulo">{{
                  t(`${F}.FIELD.BANK_ACCOUNT`)
                }}</label>
                <AdvupSelect
                  v-model="form.bank_account_id"
                  :options="bankAccountOptions"
                  min-width="100%"
                />
              </div>
            </div>
            <!-- Cada boleto que o cliente vai receber, antes de emitir: data e
               valor. O erro de divisão aparece aqui, e não no extrato. -->
            <ol
              v-if="previaDasParcelas.length"
              class="obf-previa"
              :aria-label="t(`${F}.FORM_MODAL.PREVIEW`)"
            >
              <li
                v-for="(parcela, i) in previaDasParcelas"
                :key="parcela.numero"
                class="obf-parcela advup-entra"
                :style="{ '--i': i }"
              >
                <span class="obf-parcela__num">
                  {{ parcela.numero }}/{{ previaDasParcelas.length }}
                </span>
                <span class="obf-parcela__data">{{ parcela.data }}</span>
                <b class="obf-parcela__valor">{{ parcela.valor }}</b>
              </li>
            </ol>
          </section>

          <!-- 🔴 LANÇAMENTO RETROATIVO (Victor, 10/09/2026).
             O escritório aderiu ao Asaas agora e precisa lançar o que é de
             antes — o que já foi pago por fora e o que ficou em aberto — sem
             que nada disso vire cobrança. Ver `retroactive` no controller. -->
          <!-- Baixa já lançada: modo leitura. Ver `baixaJaLancada` — o
             servidor ignora estes campos numa cobrança quitada, e campo
             editável que não salva é promessa falsa. -->
          <div v-if="baixaJaLancada" class="retro-baixa">
            <span class="retro-baixa__selo" aria-hidden="true">
              <i class="i-ph-check-circle" />
            </span>
            <div class="min-w-0">
              <p class="retro-baixa__tit">
                {{ t(`${F}.RETROACTIVE.SETTLED_TITLE`) }}
              </p>
              <p class="retro-baixa__linha">
                {{
                  t(
                    baixaJaLancada.como
                      ? `${F}.RETROACTIVE.SETTLED_LINE`
                      : `${F}.RETROACTIVE.SETTLED_LINE_NO_METHOD`,
                    {
                      valor: baixaJaLancada.valor,
                      quando: baixaJaLancada.quando,
                      como: baixaJaLancada.como,
                    }
                  )
                }}
                <template v-if="baixaJaLancada.juros">
                  {{
                    t(`${F}.RETROACTIVE.SETTLED_INTEREST`, {
                      juros: baixaJaLancada.juros,
                    })
                  }}
                </template>
              </p>
              <p class="retro-baixa__ajuda">
                {{ t(`${F}.RETROACTIVE.SETTLED_FIX`) }}
              </p>
            </div>
          </div>

          <button
            v-if="!baixaJaLancada"
            type="button"
            class="advup-chave advup-chave--ambar"
            role="switch"
            :aria-checked="String(!!form.retroactive)"
            @click="form.retroactive = !form.retroactive"
          >
            <span class="advup-chave__trilho" />
            <span class="advup-chave__txt">
              <span class="advup-chave__nome">{{
                t(`${F}.RETROACTIVE.SWITCH`)
              }}</span>
              <span class="advup-chave__sub">{{
                t(`${F}.RETROACTIVE.HINT`)
              }}</span>
            </span>
          </button>

          <div v-if="form.retroactive && !baixaJaLancada" class="retro-miolo">
            <div>
              <!-- Com parcelas, este botão deixa de ser A situação e vira um
                 atalho: cada linha decide a sua. O rótulo tem que dizer isso,
                 senão ele mente quando as linhas ficam diferentes entre si. -->
              <label class="advup-rotulo">{{
                ehParcelado
                  ? t(`${F}.RETROACTIVE.SITUATION_ALL`)
                  : t(`${F}.RETROACTIVE.SITUATION`)
              }}</label>
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  class="retro-opcao"
                  :aria-pressed="String(!!form.retroactive_paid)"
                  @click="form.retroactive_paid = true"
                >
                  {{ t(`${F}.RETROACTIVE.PAID`) }}
                </button>
                <button
                  type="button"
                  class="retro-opcao retro-opcao--aberto"
                  :aria-pressed="String(!form.retroactive_paid)"
                  @click="form.retroactive_paid = false"
                >
                  {{ t(`${F}.RETROACTIVE.OPEN`) }}
                </button>
              </div>
            </div>

            <!-- Mostra o status ANTES de salvar: ele sai da situação cruzada
               com o vencimento, e ninguém adivinha isso de cabeça. -->
            <div v-if="ehParcelado">
              <label class="advup-rotulo">{{
                t(`${F}.RETROACTIVE.RESULT_LABEL`)
              }}</label>
              <div class="flex items-center gap-2 flex-wrap">
                <span
                  v-for="r in resumoDasParcelas"
                  :key="r.chave"
                  class="ficha-pill ficha-pill--ponto"
                  :class="`retro-pill--${r.chave}`"
                >
                  {{
                    t(`${F}.RETROACTIVE.COUNT`, {
                      n: r.quantas,
                      rotulo: r.rotulo,
                    })
                  }}
                </span>
              </div>
            </div>
            <div v-else-if="situacaoRetroativa">
              <label class="advup-rotulo">{{
                t(`${F}.RETROACTIVE.RESULT_LABEL`)
              }}</label>
              <div class="flex items-center gap-2 flex-wrap">
                <span
                  class="ficha-pill ficha-pill--ponto"
                  :class="`retro-pill--${situacaoRetroativa.chave}`"
                >
                  {{ situacaoRetroativa.rotulo }}
                </span>
                <span class="advup-txt-nota">
                  {{ situacaoRetroativa.explica }}
                </span>
              </div>
            </div>

            <!-- UMA LINHA POR PARCELA. Cada uma tem a sua data, o seu valor e
               decide sozinha se já foi paga. -->
            <div v-if="ehParcelado" class="retro-parcelas">
              <div
                v-for="linha in parcelasRetro"
                :key="linha.numero"
                class="retro-linha"
                :class="{ 'retro-linha--paga': linha.paid }"
              >
                <div class="retro-linha__topo">
                  <span class="retro-numero"
                    >{{ linha.numero }}/{{ parcelasRetro.length }}</span
                  >
                  <button
                    type="button"
                    class="retro-marca"
                    :aria-pressed="String(!!linha.paid)"
                    @click="linha.paid = !linha.paid"
                  >
                    <i
                      :class="
                        linha.paid ? 'i-ph-check-circle' : 'i-ph-circle-dashed'
                      "
                    />
                    {{
                      linha.paid
                        ? t(`${F}.RETROACTIVE.PAID`)
                        : t(`${F}.RETROACTIVE.OPEN`)
                    }}
                  </button>
                </div>
                <div class="retro-linha__campos">
                  <div>
                    <label class="advup-rotulo">{{
                      linha.paid
                        ? rotuloRetro('quando')
                        : t(`${F}.FIELD.DUE_DATE`)
                    }}</label>
                    <AdvupDateTimeInput
                      v-if="linha.paid"
                      v-model="linha.realized_at"
                      date-only
                      input-class="advup-campo reset-base w-full"
                    />
                    <AdvupDateTimeInput
                      v-else
                      v-model="linha.due_date"
                      date-only
                      input-class="advup-campo reset-base w-full"
                    />
                  </div>
                  <div>
                    <label class="advup-rotulo">{{
                      linha.paid
                        ? rotuloRetro('como')
                        : t(`${F}.RETROACTIVE.AMOUNT_SHORT`)
                    }}</label>
                    <AdvupSelect
                      v-if="linha.paid"
                      v-model="linha.payment_method"
                      :options="paymentMethodOptions"
                      min-width="100%"
                    />
                    <div v-else class="advup-afixo">
                      <span class="advup-afixo__txt" aria-hidden="true">
                        {{ t(`${F}.CURRENCY_SYMBOL`) }}
                      </span>
                      <input
                        v-model="linha.gross_amount"
                        type="text"
                        inputmode="decimal"
                        class="advup-campo reset-base w-full advup-afixo__campo"
                        @blur="formatarValorDaParcela(linha)"
                      />
                    </div>
                  </div>
                  <div v-if="linha.paid">
                    <label class="advup-rotulo">{{
                      rotuloRetro('valor')
                    }}</label>
                    <div class="advup-afixo">
                      <span class="advup-afixo__txt" aria-hidden="true">
                        {{ t(`${F}.CURRENCY_SYMBOL`) }}
                      </span>
                      <input
                        v-model="linha.amount_paid"
                        type="text"
                        inputmode="decimal"
                        class="advup-campo reset-base w-full advup-afixo__campo"
                        @blur="formatarValorRecebido(linha)"
                      />
                    </div>
                  </div>
                </div>
                <!-- A conta à vista: o cliente pagou atrasado e entrou mais do
                   que a parcela. A parcela NÃO muda; a diferença é juros. -->
                <p v-if="jurosDaLinha(linha) > 0" class="retro-juros">
                  <i class="i-ph-trend-up" aria-hidden="true" />
                  {{
                    t(`${F}.RETROACTIVE.${chaveDaDivisao}`, {
                      divida: moedaComSimbolo(linha.gross_amount),
                      juros: moedaComSimbolo(jurosDaLinha(linha)),
                    })
                  }}
                </p>
              </div>
              <p class="advup-txt-nota">
                {{ rotuloRetro('caixa') }}
              </p>
            </div>

            <template v-else-if="form.retroactive_paid">
              <div class="grid grid-cols-3 gap-3">
                <div>
                  <label class="advup-rotulo">{{
                    rotuloRetro('quando')
                  }}</label>
                  <AdvupDateTimeInput
                    v-model="form.retroactive_realized_at"
                    date-only
                    input-class="advup-campo reset-base w-full"
                  />
                </div>
                <div>
                  <label class="advup-rotulo">{{ rotuloRetro('como') }}</label>
                  <AdvupSelect
                    v-model="form.retroactive_payment_method"
                    :options="paymentMethodOptions"
                    min-width="100%"
                  />
                </div>
                <!-- QUANTO entrou. Ver `linhaDoLancamentoUnico`: sem este campo
                   o valor recebido era forçado a ser igual ao devido e juros
                   nunca podiam ser registrados num lançamento de uma parcela. -->
                <div>
                  <label class="advup-rotulo">{{ rotuloRetro('valor') }}</label>
                  <div class="advup-afixo">
                    <span class="advup-afixo__txt" aria-hidden="true">
                      {{ t(`${F}.CURRENCY_SYMBOL`) }}
                    </span>
                    <input
                      v-model="form.retroactive_amount"
                      type="text"
                      inputmode="decimal"
                      class="advup-campo reset-base w-full advup-afixo__campo"
                      @blur="formatarValorRecebidoUnico"
                    />
                  </div>
                </div>
              </div>
              <!-- A mesma conta à vista do caminho parcelado: entrou mais que o
                 devido, a dívida NÃO cresce e a diferença é juros. -->
              <p v-if="jurosDoLancamentoUnico > 0" class="retro-juros">
                <i class="i-ph-trend-up" aria-hidden="true" />
                {{
                  t(`${F}.RETROACTIVE.${chaveDaDivisao}`, {
                    divida: moedaComSimbolo(form.gross_amount),
                    juros: moedaComSimbolo(jurosDoLancamentoUnico),
                  })
                }}
              </p>
              <p class="advup-txt-nota">
                {{ rotuloRetro('caixa') }}
              </p>
            </template>
          </div>

          <!-- ── Encargos por atraso ───────────────────────────────────────
             Mandados para o Asaas na emissão; nascem com o padrão do
             escritório. Somem no retroativo: encargo só existe para cobrança
             emitida, e retroativo não emite nenhuma. -->
          <section
            v-if="form.obligation_type === 'receivable' && !form.retroactive"
            class="advup-secao obf-encargos"
          >
            <h3 class="advup-secao__tit">
              <span
                class="advup-secao__ico advup-secao__ico--ambar"
                aria-hidden="true"
              >
                <i class="i-ph-percent" />
              </span>
              {{ t(`${F}.CHARGES.TITLE`) }}
            </h3>
            <p class="advup-txt-nota">{{ t(`${F}.CHARGES.HELP`) }}</p>
            <div class="obf-grade">
              <div>
                <label for="obf-multa" class="advup-rotulo">
                  {{ t(`${F}.CHARGES.FINE`) }}
                </label>
                <div class="advup-afixo">
                  <input
                    id="obf-multa"
                    v-model="form.fine_percent"
                    type="text"
                    inputmode="decimal"
                    class="advup-campo reset-base w-full advup-afixo__campo advup-afixo__campo--fim"
                    :placeholder="t(`${F}.CHARGES.PLACEHOLDER`)"
                  />
                  <span
                    class="advup-afixo__txt advup-afixo__txt--fim"
                    aria-hidden="true"
                  >
                    %
                  </span>
                </div>
              </div>
              <div>
                <label for="obf-juros" class="advup-rotulo">
                  {{ t(`${F}.CHARGES.INTEREST`) }}
                </label>
                <div class="advup-afixo">
                  <input
                    id="obf-juros"
                    v-model="form.interest_percent"
                    type="text"
                    inputmode="decimal"
                    class="advup-campo reset-base w-full advup-afixo__campo advup-afixo__campo--fim"
                    :placeholder="t(`${F}.CHARGES.PLACEHOLDER`)"
                  />
                  <span
                    class="advup-afixo__txt advup-afixo__txt--fim"
                    aria-hidden="true"
                  >
                    %
                  </span>
                </div>
              </div>
            </div>
            <div>
              <label class="advup-rotulo">{{
                t(`${F}.CHARGES.BILLING`)
              }}</label>
              <!-- Duas opções, e nenhuma delas libera cartão: "cliente escolhe"
                 mandava UNDEFINED ao Asaas e abria crédito (saiu em
                 09/09/2026). -->
              <div
                class="advup-seg"
                role="radiogroup"
                :aria-label="t(`${F}.CHARGES.BILLING`)"
              >
                <button
                  v-for="opcao in billingTypeOptions"
                  :key="opcao.value"
                  type="button"
                  role="radio"
                  class="advup-seg__btn"
                  :class="{
                    'advup-seg__btn--on': form.billing_type === opcao.value,
                  }"
                  :aria-checked="String(form.billing_type === opcao.value)"
                  @click="form.billing_type = opcao.value"
                >
                  <i
                    :class="ICONE_DA_COBRANCA[opcao.value]"
                    aria-hidden="true"
                  />
                  {{ opcao.label }}
                </button>
              </div>
              <p class="advup-txt-nota obf-dica">
                {{ t(`${F}.CHARGES.BILLING_HINT`) }}
              </p>
            </div>
            <p class="advup-txt-nota obf-dica">
              <i class="i-ph-info" aria-hidden="true" />
              {{ t(`${F}.CHARGES.ZERO_NOTE`) }}
            </p>
          </section>

          <!-- ── Quem e onde ──────────────────────────────────────────────── -->
          <section class="advup-secao">
            <h3 class="advup-secao__tit">
              <span class="advup-secao__ico" aria-hidden="true">
                <i class="i-ph-users-three" />
              </span>
              {{ t(`${F}.FORM_MODAL.SECTION_WHO`) }}
            </h3>
            <div>
              <label for="obf-cliente" class="advup-rotulo">{{
                t(`${F}.FIELD.CREDITOR_DEBTOR`)
              }}</label>
              <div
                v-if="form.creditor_debtor_id"
                class="advup-campo advup-campo--linha obf-escolhido"
              >
                <span
                  class="advup-avatar advup-avatar--p"
                  :class="`advup-avatar--${corDoNome(form.creditor_debtor_name)}`"
                  aria-hidden="true"
                >
                  {{ iniciaisDoNome(form.creditor_debtor_name) }}
                </span>
                <span class="obf-escolhido__nome">{{
                  form.creditor_debtor_name
                }}</span>
                <button
                  type="button"
                  class="advup-campo__limpar"
                  :aria-label="t(`${F}.FORM_MODAL.CLEAR_CONTACT`)"
                  @click="clearContact"
                >
                  <i class="i-ph-x" />
                </button>
              </div>
              <p
                v-if="quemRecebeACobranca"
                class="advup-nota obf-representante"
              >
                <i class="i-ph-users-three" aria-hidden="true" />
                {{
                  t(`${F}.FORM_MODAL.BILLED_TO_REPRESENTATIVE`, {
                    nome: quemRecebeACobranca.name,
                    grau: grauDoRepresentante(quemRecebeACobranca.relationship),
                  })
                }}
              </p>
              <div
                v-if="pagadorIncompleto"
                class="advup-aviso advup-aviso--rubi obf-pagador-incompleto"
                role="alert"
              >
                <i
                  class="i-ph-warning-octagon advup-aviso__ico"
                  aria-hidden="true"
                />
                <p class="advup-aviso__txt">{{ pagadorIncompleto.mensagem }}</p>
                <button
                  v-if="pagadorIncompleto.pagador"
                  type="button"
                  class="advup-aviso__acao"
                  @click="abrirFichaDoPagador"
                >
                  {{ t(`${F}.FORM_MODAL.OPEN_PAYER_RECORD`) }}
                </button>
              </div>
              <div v-if="!form.creditor_debtor_id" class="relative">
                <input
                  id="obf-cliente"
                  v-model="contactQuery"
                  type="text"
                  class="advup-campo reset-base w-full"
                  :placeholder="t(`${F}.SEARCH_CONTACT`)"
                  @input="onContactInput"
                  @blur="fecharSugestoes('cliente')"
                  @keydown.esc="fecharSugestoes('cliente')"
                />
                <div
                  v-if="contactResults.length"
                  class="advup-menu advup-pop obf-sugestoes"
                >
                  <button
                    v-for="c in contactResults.slice(0, 6)"
                    :key="c.id"
                    type="button"
                    class="advup-menu__item"
                    @mousedown.prevent
                    @click="pickContact(c)"
                  >
                    <span
                      class="advup-avatar advup-avatar--p"
                      :class="`advup-avatar--${corDoNome(c.name)}`"
                      aria-hidden="true"
                    >
                      {{ iniciaisDoNome(c.name) }}
                    </span>
                    {{ c.name }}
                  </button>
                </div>
              </div>
            </div>
            <div>
              <label for="obf-pasta" class="advup-rotulo">{{
                t(`${F}.FIELD.OWNER`)
              }}</label>
              <div
                v-if="form.owner_id"
                class="advup-campo advup-campo--linha obf-escolhido"
              >
                <i
                  class="i-ph-folder-simple obf-escolhido__ico"
                  aria-hidden="true"
                />
                <span class="obf-escolhido__nome">{{ form.owner_label }}</span>
                <button
                  type="button"
                  class="advup-campo__limpar"
                  :aria-label="t(`${F}.FORM_MODAL.CLEAR_OWNER`)"
                  @click="clearOwner"
                >
                  <i class="i-ph-x" />
                </button>
              </div>
              <div v-else class="relative">
                <input
                  id="obf-pasta"
                  v-model="ownerQuery"
                  type="text"
                  class="advup-campo reset-base w-full"
                  :disabled="!form.creditor_debtor_id"
                  :placeholder="
                    form.creditor_debtor_id
                      ? t(`${F}.SEARCH_OWNER`)
                      : t(`${F}.SELECT_CLIENT_FIRST`)
                  "
                  @input="onOwnerInput"
                  @focus="loadOwnersForContact"
                  @click="reabrirSugestoes('pasta', loadOwnersForContact)"
                  @blur="fecharSugestoes('pasta')"
                  @keydown.esc="fecharSugestoes('pasta')"
                />
                <div
                  v-if="form.creditor_debtor_id && ownerResults.length"
                  class="advup-menu advup-pop obf-sugestoes"
                >
                  <button
                    v-for="p in ownerResults.slice(0, 8)"
                    :key="p.id"
                    type="button"
                    class="advup-menu__item"
                    @mousedown.prevent
                    @click="pickOwner(p)"
                  >
                    <i class="i-ph-folder-simple" aria-hidden="true" />
                    {{ ownerLabel(p) }}
                  </button>
                </div>
                <p
                  v-else-if="
                    form.creditor_debtor_id &&
                    !ownerLoading &&
                    !ownerResults.length
                  "
                  class="advup-txt-nota obf-dica"
                >
                  {{ t(`${F}.NO_OWNER_FOR_CLIENT`) }}
                </p>
              </div>
            </div>
            <!-- Parceiro do repasse. Só em conta A PAGAR, e opcional: nota
               fiscal e despesa fixa também são a pagar e não têm parceiro. -->
            <div v-if="ehAPagar">
              <label for="obf-parceiro" class="advup-rotulo">{{
                t(`${F}.FIELD.PARTNER`)
              }}</label>
              <div
                v-if="form.partner_id"
                class="advup-campo advup-campo--linha obf-escolhido"
              >
                <i
                  class="i-ph-handshake obf-escolhido__ico"
                  aria-hidden="true"
                />
                <span class="obf-escolhido__nome">{{ partnerName }}</span>
                <button
                  type="button"
                  class="advup-campo__limpar"
                  :aria-label="t(`${F}.FIELD.PARTNER_CLEAR`)"
                  @click="clearPartner"
                >
                  <i class="i-ph-x" />
                </button>
              </div>
              <!-- `relative` no pai e a lista `absolute` por cima, senão ela
                 empurra o resto do formulário a cada tecla. -->
              <div v-else class="relative">
                <input
                  id="obf-parceiro"
                  v-model="partnerQuery"
                  type="text"
                  class="advup-campo reset-base w-full"
                  :placeholder="t(`${F}.FIELD.PARTNER_SEARCH`)"
                  @input="onPartnerInput"
                  @focus="onPartnerFocus"
                  @click="reabrirSugestoes('parceiro', onPartnerFocus)"
                  @blur="fecharSugestoes('parceiro')"
                  @keydown.esc="fecharSugestoes('parceiro')"
                />
                <div
                  v-if="partnerResults.length"
                  class="advup-menu advup-pop obf-sugestoes"
                >
                  <button
                    v-for="pt in partnerResults"
                    :key="pt.id"
                    type="button"
                    class="advup-menu__item"
                    @mousedown.prevent
                    @click="pickPartner(pt)"
                  >
                    <i class="i-ph-handshake" aria-hidden="true" />
                    {{ pt.name }}
                  </button>
                </div>
              </div>
              <p class="advup-txt-nota obf-dica">
                {{ t(`${F}.FIELD.PARTNER_HINT`) }}
              </p>
            </div>
            <div>
              <label for="obf-notas" class="advup-rotulo">{{
                t(`${F}.FIELD.NOTES`)
              }}</label>
              <textarea
                id="obf-notas"
                v-model="form.notes"
                rows="2"
                class="advup-campo advup-campo--area reset-base w-full"
              />
            </div>
            <!-- Anexos já no formulário (Dra. Jullya, 06/10/2026: "juntar o
               RPV"). Antes só existiam no detalhe, depois de salvo. Ao criar,
               os arquivos esperam e sobem com o lançamento. -->
            <ListaDeAnexos
              ref="listaDeAnexos"
              :registro-id="editing?.id || null"
              :api="attachmentsAPI"
            />
          </section>
        </div>
        <div class="advup-modal__rodape advup-modal__rodape--acoes">
          <button
            type="button"
            class="advup-btn advup-btn--vazado"
            @click="closeModal"
          >
            {{ t(`${F}.CANCEL`) }}
          </button>
          <button
            type="button"
            class="advup-btn-forte"
            :disabled="saving"
            @click="save"
          >
            <i
              :class="
                saving ? 'i-ph-circle-notch advup-gira' : 'i-ph-check-bold'
              "
              aria-hidden="true"
            />
            {{ saving ? t(`${F}.SAVING`) : t(`${F}.SAVE`) }}
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
/* Anexos no formulário alinham com os campos: o recuo da lista é para
   quando ela mora dentro de um cartão (detalhe do lançamento). */
:deep(.lista-anexos .advup-doc-lista) {
  padding: 0;
}

/* A chave do lançamento retroativo usa âmbar, e não o azul da marca, porque
   ela não é "mais uma opção": é o aviso de que aquela obrigação NÃO vira
   cobrança. O bloco tracejado abaixo repete o mesmo tom para dizer que os
   dois pertencem um ao outro. */
.retro-miolo {
  display: flex;
  flex-direction: column;
  gap: var(--esp-3);
  padding: var(--esp-3);
  border: 1px dashed rgb(var(--amber-7));
  border-radius: var(--r-4);
}
/* A baixa já lançada. Verde e SÓLIDO, contra o âmbar tracejado do retroativo
   que ainda está sendo preenchido: um é fato consumado, o outro é rascunho, e
   a borda diz qual é qual antes de ler. */
.retro-baixa {
  display: flex;
  gap: var(--esp-3);
  align-items: flex-start;
  padding: var(--esp-3) var(--esp-3);
  border: 1px solid rgb(var(--teal-6));
  border-radius: var(--r-4);
  background: rgb(var(--teal-2));
}
.retro-baixa__selo {
  display: grid;
  flex: none;
  place-items: center;
  width: 26px;
  height: 26px;
  border-radius: var(--r-redondo);
  background: rgb(var(--teal-3));
  color: rgb(var(--teal-11));
  font-size: var(--txt-lista);
}
.retro-baixa__tit {
  font-size: var(--txt-corpo);
  font-weight: var(--peso-forte);
  color: rgb(var(--teal-12));
}
.retro-baixa__linha {
  margin-top: 0;
  font-size: var(--txt-nota);
  color: rgb(var(--slate-12));
}
.retro-baixa__ajuda {
  margin-top: var(--esp-1);
  font-size: var(--txt-selo);
  line-height: var(--linha-normal);
  color: rgb(var(--slate-11));
}
.retro-opcao {
  padding: var(--esp-1) var(--esp-3);
  border: 1px solid rgb(var(--slate-6));
  border-radius: var(--r-redondo);
  background: rgb(var(--slate-1));
  color: rgb(var(--slate-11));
  font-size: var(--txt-nota);
  font-weight: var(--peso-dado);
  cursor: pointer;
}
.retro-opcao[aria-pressed='true'] {
  border-color: rgb(var(--teal-9));
  background: rgb(var(--teal-2));
  color: rgb(var(--teal-11));
  font-weight: var(--peso-forte);
}
.retro-opcao--aberto[aria-pressed='true'] {
  border-color: rgb(var(--blue-9));
  background: rgb(var(--blue-2));
  color: rgb(var(--blue-11));
}
/* Os mesmos tons da pílula de status na lista, para o que se vê aqui ser o
   que se vê depois. */
/* O "R$" é adorno do campo, não conteúdo: fica fora do valor digitado, senão
   ele viraria texto para o parser toda vez. */
.retro-parcelas {
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
}
.retro-linha {
  padding: var(--esp-2) var(--esp-3);
  border: 1px solid rgb(var(--slate-4));
  border-radius: var(--r-3);
  background: rgb(var(--slate-1));
}
/* Paga fica com a borda teal: dá para varrer a lista e ver o que já entrou
   sem ler campo nenhum. */
.retro-linha--paga {
  border-color: rgb(var(--teal-7));
  background: rgb(var(--teal-1));
}
.retro-linha__topo {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--esp-2);
  margin-bottom: var(--esp-2);
}
.retro-numero {
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
  color: rgb(var(--slate-11));
  font-variant-numeric: tabular-nums;
}
.retro-marca {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-1);
  padding: var(--esp-1) var(--esp-2);
  border: 1px solid rgb(var(--slate-6));
  border-radius: var(--r-redondo);
  background: rgb(var(--slate-1));
  color: rgb(var(--slate-11));
  font-size: var(--txt-nota);
  font-weight: var(--peso-dado);
  cursor: pointer;
}
.retro-marca[aria-pressed='true'] {
  border-color: rgb(var(--teal-9));
  background: rgb(var(--teal-2));
  color: rgb(var(--teal-11));
  font-weight: var(--peso-forte);
}
.retro-linha__campos {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: var(--esp-2);
}
/* Âmbar, não vermelho: juros recebido é dinheiro que ENTROU, não problema.
   Vermelho assustaria quem está só registrando o passado. */
.retro-juros {
  display: flex;
  align-items: center;
  gap: var(--esp-1);
  margin-top: var(--esp-2);
  font-size: var(--txt-nota);
  color: rgb(var(--amber-11));
}
.retro-juros > i {
  width: 13px;
  height: 13px;
}
.retro-pill--settled {
  background: rgb(var(--teal-3));
  color: rgb(var(--teal-11));
}
/* Vencida é âmbar e inadimplente é rubi — os mesmos tons da lista, e na
   mesma ordem de gravidade: a régua ainda fala com o vencido, e já parou de
   falar com o inadimplente. */
.retro-pill--overdue {
  background: rgb(var(--amber-3));
  color: rgb(var(--amber-11));
}
.retro-pill--inadimplente {
  background: rgb(var(--ruby-3));
  color: rgb(var(--ruby-11));
}
.retro-pill--pending {
  background: rgb(var(--slate-3));
  color: rgb(var(--slate-11));
}

/* ── A cara do formulário ─────────────────────────────────────────────── */
.obf {
  display: flex;
  flex-direction: column;
  gap: var(--esp-5);
}

/* O ícone do topo troca com um pulo quando o tipo muda. */
.obf-topo__ico {
  animation: obf-pula var(--mov-3) var(--mov-curva-pulo) both;
}

/* O fio depois do título amarra o rótulo ao bloco. */

.obf-grade {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--esp-3);
}

.obf-dica {
  display: flex;
  align-items: center;
  gap: var(--esp-1);
  margin: var(--esp-1) 0 0;
  color: rgb(var(--slate-11));
}

.obf-dica > i {
  display: block;
  flex: none;
  font-size: var(--ico-1);
  line-height: 1;
}

/* ── Os tipos ─────────────────────────────────────────────────────────── */
.obf-tipos {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--esp-2);
}

/* ── A prévia das parcelas ────────────────────────────────────────────── */
.obf-previa {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: var(--esp-2);
  margin: 0;
  padding: 0;
  list-style: none;
}

.obf-parcela {
  display: flex;
  flex-direction: column;
  gap: var(--esp-1);
  padding: var(--esp-2) var(--esp-3);
  border: 1px solid rgb(var(--slate-4));
  border-radius: var(--r-3);
  background: rgb(var(--solid-1));
  font-variant-numeric: tabular-nums;
}

.obf-parcela__num {
  font-size: var(--txt-rotulo);
  font-weight: var(--peso-rotulo);
  line-height: var(--linha-apertada);
  letter-spacing: var(--rastro-rotulo);
  color: rgb(var(--slate-11));
}

.obf-parcela__data {
  font-size: var(--txt-nota);
  line-height: var(--linha-solta);
  color: rgb(var(--slate-11));
}

.obf-parcela__valor {
  font-family: var(--fonte-display);
  font-size: var(--txt-lista);
  font-weight: var(--peso-num);
  line-height: var(--linha-num);
  color: rgb(var(--slate-12));
}

/* ── Encargos ─────────────────────────────────────────────────────────── */
.obf-encargos {
  padding: var(--esp-4);
  border: 1px solid rgb(var(--amber-5));
  border-radius: var(--r-4);
  background: radial-gradient(
      110% 130% at 0% 0%,
      rgb(var(--amber-2)) 0%,
      transparent 55%
    ),
    rgb(var(--solid-1));
}

.dark .obf-encargos {
  background: radial-gradient(
      110% 130% at 0% 0%,
      color-mix(in srgb, rgb(var(--amber-9)) 10%, transparent) 0%,
      transparent 55%
    ),
    rgb(var(--solid-1));
}

/* O "%" é adorno do campo, como o "R$": fora do texto digitado. */

/* ── Quem e onde ──────────────────────────────────────────────────────── */
/* O aviso de ficha incompleta fica colado no cliente, como a nota do
   representante: é aquela escolha que impede o salvamento. */
.obf-pagador-incompleto {
  margin-top: var(--esp-2);
}

/* A nota de quem recebe a cobrança fica colada no campo do cliente: é sobre
   aquela escolha, não sobre o formulário. */
.obf-representante {
  display: flex;
  align-items: flex-start;
  gap: var(--esp-1);
  margin: var(--esp-1) 0 0;
  color: rgb(var(--blue-11));
}

.obf-escolhido {
  gap: var(--esp-2);
}

.obf-escolhido__ico {
  display: block;
  flex: none;
  font-size: var(--ico-2);
  line-height: 1;
  color: rgb(var(--blue-11));
}

.obf-escolhido__nome {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: rgb(var(--slate-12));
}

.obf-sugestoes {
  position: absolute;
  top: calc(100% + var(--esp-1));
  right: 0;
  left: 0;
  z-index: 20;
  max-height: 280px;
  overflow-y: auto;
}

@keyframes obf-pula {
  from {
    opacity: 0;
    transform: scale(0.6) rotate(-10deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .obf-topo__ico {
    animation: none;
  }
}

/* No FIM: `@media` não soma especificidade. */
@media (max-width: 639px) {
  .obf-grade {
    grid-template-columns: minmax(0, 1fr);
  }

  .obf-tipos {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
